<template>
  <section
    id="bonus-wallet"
    :class="['bonus-wallet', { 'loading-block': loading }]"
    data-qa="bonus-wallet-container"
  >
    <div class="bonus-wallet__error" v-if="notificationError">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M10.0082 6.66651V10.8332M10.0082 13.3332H9.99984M18.3332 9.99984C18.3332 14.6022 14.6022 18.3332 9.99984 18.3332C5.39746 18.3332 1.6665 14.6022 1.6665 9.99984C1.6665 5.39746 5.39746 1.6665 9.99984 1.6665C14.6022 1.6665 18.3332 5.39746 18.3332 9.99984Z"
          stroke="#ED7F2F"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <p>{{ $t('checkout_pick_payment_notify') }}</p>
    </div>

    <div class="bonus-wallet__body body">
      <h3 class="body__balance" data-qa="bonus-wallet-balance">
        {{ balanceTitle }}
      </h3>

      <p class="body__text" data-qa="bonus-wallet-text">
        <span>{{ stateText.plain }}</span>
        <strong v-if="stateText.strong">{{ ' ' + stateText.strong }}</strong>
      </p>

      <!-- Состояние 1: доступно списание, сумма по умолчанию (максимально возможная) -->
      <button
        v-if="isDefaultState"
        class="body__link"
        @click="openCustomAmount"
        data-qa="bonus-wallet-another-amount-link"
      >
        {{ $t('coral_wallet_another_amount') }}
      </button>

      <!-- Состояние 2: пользователь вводит свою сумму -->
      <div class="body__form" v-if="isCustomAmountState">
        <label for="coin-input">
          {{ $t('coral_wallet_enter_amount') }}
        </label>
        <div class="body__form-input">
          <input
            id="coin-input"
            type="number"
            step="0.01"
            min="0"
            :max="wallet.CW_VALUE_LIMIT"
            :value="value"
            @keydown="blockNonNumericKeys"
            @input="inputWithValidate"
            @blur="roundCurrentValue"
            data-qa="bonus-wallet-coins-input"
          />
          <button
            class="body__btn-apply"
            :disabled="applyDisabled"
            @click="applyDiscount"
            data-qa="bonus-wallet-apply-btn"
          >
            {{ $t('coral_wallet_apply') }}
          </button>
        </div>
      </div>

      <!-- Состояния 1: кнопка применения списания по умолчанию -->
      <button
        v-if="isDefaultState"
        class="body__btn-apply body__btn-apply--standalone"
        :disabled="applyDisabled"
        @click="applyDiscount"
        data-qa="bonus-wallet-apply-btn"
      >
        {{ $t('coral_wallet_apply') }}
      </button>

      <!-- Состояния 3 и 4: списание применено -->
      <button
        v-if="isPaid"
        class="body__btn-remove"
        @click="removeDiscount"
        data-qa="bonus-wallet-remove-btn"
      >
        {{ $t('coral_wallet_remove') }}
      </button>

      <div class="loader">
        <Loader />
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { formatMoneyGlobal } from '@/utils/delivery'
import Loader from './Loader.vue'

export default {
  name: 'BonusWallet',
  components: {
    Loader,
  },
  props: {
    notificationError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isApplied: false,
      isPaid: false,
      isCustomAmount: false,
      customValue: null,
    }
  },
  computed: {
    ...mapState(['loading', 'wallet', 'orderData', 'serviceState']),
    value() {
      return this.customValue ?? this.wallet.CW_VALUE_LIMIT
    },
    applyDisabled() {
      return this.value <= 0 || this.isApplied
    },
    isDefaultState() {
      return (
        !this.isPaid && !this.isCustomAmount && Boolean(this.wallet.CW_VALUE)
      )
    },
    isCustomAmountState() {
      return !this.isPaid && this.isCustomAmount
    },
    restToPay() {
      return Number(this.orderData.TOTAL_PRICE_WITH_PROMO) || 0
    },
    appliedPoints() {
      return Number(this.orderData.BONUS_VALUE) || 0
    },
    canCoverFullOrder() {
      return Number(this.wallet.CW_VALUE_LIMIT) >= this.restToPay
    },
    balance() {
      if (!this.orderData.CW_VALUE) return 0
      return this.orderData.CW_VALUE - this.orderData.BONUS_VALUE
    },
    balanceTitle() {
      return `Coral Wallet ${this.$t(
        'coral_wallet_balance'
      )} ${this.formatCurrency(this.balance)}`
    },
    stateText() {
      if (this.isPaid) {
        if (this.restToPay === 0) {
          return {
            plain: this.$t('coral_wallet_fully_paid'),
            strong: '',
          }
        }
        const applied = this.formatCurrency(this.appliedPoints)
        const rest = this.formatCurrency(this.restToPay)

        return {
          plain: this.$t('coral_wallet_applied')?.replace('{amount}', applied),
          strong: this.$t('coral_wallet_rest_to_pay')?.replace('{rest}', rest),
        }
      }
      if (this.isCustomAmount) {
        return {
          plain: this.$t('coral_wallet_choose_amount'),
          strong: '',
        }
      }
      if (this.canCoverFullOrder) {
        return {
          plain: this.$t('coral_wallet_can_cover_all'),
          strong: this.$t('coral_wallet_apply_full'),
        }
      }
      const max = this.formatCurrency(this.wallet.CW_VALUE_LIMIT)

      return {
        plain: this.$t('coral_wallet_can_cover_part')?.replace('{amount}', max),
        strong: this.$t('coral_wallet_pay_rest_another'),
      }
    },
  },
  methods: {
    ...mapMutations({
      setUseValue: 'wallet/setUseValue',
    }),
    roundByStep(value) {
      if (!this.wallet.CW_VALUE_ROUND_STEP) return value

      const stepNum = parseFloat(
        String(this.wallet.CW_VALUE_ROUND_STEP).replace(',', '.')
      )
      // Проверка на NaN сразу после parseFloat
      if (isNaN(stepNum) || stepNum <= 0) return value

      // Определяем количество знаков после запятой в шаге
      const stepStr = stepNum.toString()
      const decimalIndex = stepStr.indexOf('.')
      const decimals =
        decimalIndex === -1 ? 0 : stepStr.length - decimalIndex - 1
      // Ограничиваем decimals для предотвращения потери точности (max 8 знаков)
      const safeDecimals = Math.min(decimals, 8)
      // Округляем значение до ближайшего кратного шага
      const rounded = Math.round(value / stepNum) * stepNum

      // Фиксируем количество знаков после запятой
      return Number(rounded.toFixed(safeDecimals))
    },
    // Буквы type="number" отсекает сам, но «e», «E», «+», «-» считает частью числа и пропускает.
    blockNonNumericKeys(e) {
      if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault()
    },
    inputWithValidate(e) {
      let raw = e.target.value
      // 1. защита от ведущего 0 без точки/запятой: "01", "02" и т.п.
      const invalidLeadingZero =
        raw.length > 1 &&
        raw.startsWith('0') &&
        !raw.includes(',') &&
        !raw.includes('.')

      if (invalidLeadingZero && e.data != null) {
        raw = e.data
        e.target.value = raw
      }

      const normalizedRaw = raw.replace(',', '.')
      let inputValue = parseFloat(normalizedRaw)
      this.isApplied = false

      // если не число — просто сохраняем как есть (даём пользователю дописать)
      if (isNaN(inputValue)) {
        this.customValue = raw
        return
      }

      if (inputValue > this.wallet.CW_VALUE_LIMIT) {
        this.customValue = e.target.value = String(this.wallet.CW_VALUE_LIMIT)

        return
      }

      // Во время ввода не округляем
      this.customValue = raw
    },

    roundCurrentValue(e) {
      if (!this.wallet.CW_VALUE_ROUND_STEP) return
      const numeric = parseFloat(String(this.value ?? '').replace(',', '.'))
      if (isNaN(numeric)) return
      const rounded = this.roundByStep(numeric)
      if (rounded === numeric) return
      const roundedStr = String(rounded)
      this.customValue = roundedStr
      if (e?.target) e.target.value = roundedStr
    },
    openCustomAmount() {
      this.customValue = null
      this.isCustomAmount = true
    },
    applyDiscount() {
      this.roundCurrentValue()
      this.isPaid = true
      this.isCustomAmount = false
      this.isApplied = true
      this.setUseValue(this.value)
      this.$emit('useCoralWallet')
    },
    removeDiscount() {
      this.setUseValue(0)
      this.isPaid = false
      this.isCustomAmount = false
      this.isApplied = false
      this.customValue = null
      this.$emit('useCoralWallet')
    },
    formatCurrency(value) {
      return formatMoneyGlobal(
        value,
        this.$store.state.country.code,
        this.$store.state.currency,
        { minimumFractionDigits: 0, maximumFractionDigits: 2 }
      )
    },
  },
  watch: {
    loading() {
      this.isApplied = false
    },
    serviceState() {
      // При изменении профиля/сервиса/типа доставки сбрасываем wallet
      if (this.isPaid || this.isCustomAmount) {
        this.setUseValue(0)
        this.isPaid = false
        this.isCustomAmount = false
        this.isApplied = false
        this.customValue = null
        this.$emit('useCoralWallet')
      }
    },
  },
}
</script>

<style lang="scss">
.bonus-wallet {
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  &__error {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px;
    background: #faeadf;
    color: #6c7885;
    border-radius: 10px;
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
  }

  .body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;

    &__balance {
      font-size: 24px;
      font-weight: 600;
      line-height: 28px;
      color: #39444c;

      @media (max-width: 480px) {
        font-size: 20px;
        line-height: 24px;
      }
    }

    &__text {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: -0.014em;
      color: #39444c;

      strong {
        font-weight: 600;
      }
    }

    &__link {
      padding: 0;
      border: none;
      outline: none;
      background: transparent;
      color: #858c91;
      font-size: 16px;
      line-height: 20px;
      text-decoration: underline;
      cursor: pointer;
      margin-bottom: 10px;
    }

    &__form {
      display: flex;
      flex-direction: column;
      gap: 4px;
      width: 100%;

      label {
        color: #666f76;
        font-size: 14px;
        font-weight: 400;
        line-height: 17px;
      }

      &-input {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 44px;

        @media (max-width: 480px) {
          flex-direction: column;
          align-items: stretch;
          height: auto;
        }

        input {
          max-width: 296px;
          height: 100%;
          flex-grow: 1;
          border: 1px solid #dcdcdc;
          border-radius: 4px;
          padding: 12px;
          outline: none;
          &::-webkit-outer-spin-button,
          &::-webkit-inner-spin-button {
            display: none;
          }

          @media (max-width: 480px) {
            max-width: 100%;
            height: 44px;
          }
        }

        .body__btn-apply {
          flex-shrink: 0;

          @media (max-width: 480px) {
            width: 100%;
            padding: 10px 12px;
          }
        }
      }
    }

    &__btn-apply {
      text-align: center;
      height: 44px;
      padding: 10px 18px;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      color: #d6483d;
      background: rgba(253, 201, 194, 0.25);
      border-radius: 8px;
      border: none;
      outline: none;
      white-space: nowrap;
      cursor: pointer;

      &:disabled {
        background: #f8f9f9;
        color: #dcdcdc;
        border: 1px solid #dcdcdc;
        cursor: default;
      }
    }

    &__btn-remove {
      height: 44px;
      padding: 10px 18px;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      color: #39444c;
      background: #ffffff;
      border: 1px solid #dcdcdc;
      border-radius: 8px;
      outline: none;
      white-space: nowrap;
      cursor: pointer;
    }

    &__btn-apply--standalone,
    &__btn-remove {
      @media (max-width: 480px) {
        width: 100%;
      }
    }
  }

  .loader {
    margin: 0 !important;
  }
}
</style>
